import React, { useState, useEffect, useCallback } from "react";
import { adminApi } from "../api";
import AdminSelect from "./AdminSelect";

/* ── Tiny icons ──────────────────────────── */
const Ico = {
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
  save: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  info: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
};

const CONTEXTS = [
  { value: "new", label: "New member signup" },
  { value: "renewal", label: "Membership renewal" },
  { value: "update", label: "Profile update" },
];

const BEHAVIORS = [
  { value: "fixed", label: "Always send a fixed value", desc: "Same value every time, no matter which product" },
  { value: "preserve", label: "Keep the member's existing value", desc: "Re-send what's already in S7000 (falls back to default)" },
  { value: "product_mapped", label: "Use the value from the product", desc: "Each product defines its own value (set in product edit form)" },
];

export default function AnalysisCodesTab({ venueId, providerType, api = adminApi, canManageProducts = true }) {
  const [types, setTypes] = useState([]);
  const [rules, setRules] = useState([]);
  const [products, setProducts] = useState([]);         // original from server
  const [productCodes, setProductCodes] = useState({}); // { [productId]: [{ typecode, valuecode }, ...] }  — editable copy
  const [savingProducts, setSavingProducts] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", msg: "" });
  const [loaded, setLoaded] = useState(false);
  const [expandedType, setExpandedType] = useState(null);

  const flash = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback({ type: "", msg: "" }), 4000);
  };

  // Group types by typecode for dropdown rendering
  const typeGroups = React.useMemo(() => {
    const map = {};
    for (const t of types) {
      if (!map[t.typecode]) map[t.typecode] = { typecode: t.typecode, name: t.name, values: [] };
      map[t.typecode].values.push({ valuecode: t.valuecode, label: t.value_label });
    }
    return Object.values(map).sort((a, b) => {
      const na = parseInt(a.typecode), nb = parseInt(b.typecode);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.typecode.localeCompare(b.typecode);
    });
  }, [types]);

  const loadData = useCallback(async () => {
    try {
      const [t, r, p] = await Promise.all([
        api.getAnalysisTypes(venueId),
        api.getAnalysisRules(venueId),
        api.getVenueProducts(venueId),
      ]);
      setTypes(t || []);
      setRules(r || []);
      const prods = Array.isArray(p) ? p : (p?.products || []);
      setProducts(prods);
      // seed the editable copy — clone so user edits don't mutate props
      const codesMap = {};
      for (const prod of prods) {
        codesMap[prod.id] = Array.isArray(prod.analysis_codes)
          ? prod.analysis_codes.map(c => ({ ...c }))
          : [];
      }
      setProductCodes(codesMap);
      setLoaded(true);
    } catch (err) {
      flash("error", err.message);
    }
  }, [venueId]);

  useEffect(() => { loadData(); }, [loadData]);

  async function fetchFromS7000() {
    setFetching(true);
    try {
      const res = await api.fetchAnalysisCodes(venueId);
      flash("success", `Fetched ${res.count} type/value combinations from S7000`);
      await loadData();
    } catch (err) {
      flash("error", err.message);
    }
    setFetching(false);
  }

  async function saveRules() {
    setSaving(true);
    try {
      const saved = await api.saveAnalysisRules(venueId, rules);
      setRules(saved);
      flash("success", "Analysis rules saved");
    } catch (err) {
      flash("error", err.message);
    }
    setSaving(false);
  }

  function addRule() {
    setRules([...rules, { typecode: typeGroups[0]?.typecode || "", context: "new", behavior: "fixed", default_valuecode: "" }]);
  }

  function updateRule(idx, field, value) {
    setRules(rules.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  }

  function removeRule(idx) {
    setRules(rules.filter((_, i) => i !== idx));
  }

  /* ── Product-code inline editing ──────────── */

  // Read the current (edited) valuecode for a product + typecode, or "".
  function getProductCode(productId, typecode) {
    const codes = productCodes[productId] || [];
    const entry = codes.find(c => c && c.typecode === typecode);
    return entry ? entry.valuecode || "" : "";
  }

  // Update the editable copy for a product + typecode.
  function setProductCode(productId, typecode, valuecode) {
    setProductCodes(prev => {
      const list = [...(prev[productId] || [])];
      const idx = list.findIndex(c => c && c.typecode === typecode);
      if (!valuecode) {
        // Empty selection → remove mapping
        if (idx >= 0) list.splice(idx, 1);
      } else if (idx >= 0) {
        list[idx] = { typecode, valuecode };
      } else {
        list.push({ typecode, valuecode });
      }
      return { ...prev, [productId]: list };
    });
  }

  // Deep-equal for analysis_codes arrays (order-independent by typecode).
  function codesEqual(a = [], b = []) {
    const norm = (arr) =>
      [...(arr || [])]
        .filter(x => x && x.typecode)
        .sort((x, y) => x.typecode.localeCompare(y.typecode))
        .map(x => `${x.typecode}=${x.valuecode || ""}`)
        .join("|");
    return norm(a) === norm(b);
  }

  // Products whose analysis_codes differ from the server copy.
  const dirtyProductIds = React.useMemo(() => {
    return products
      .filter(p => !codesEqual(p.analysis_codes || [], productCodes[p.id] || []))
      .map(p => p.id);
  }, [products, productCodes]);

  async function saveProductCodes() {
    if (!dirtyProductIds.length) return;
    setSavingProducts(true);
    try {
      for (const pid of dirtyProductIds) {
        await api.updateVenueProduct(venueId, pid, {
          analysis_codes: productCodes[pid] || [],
        });
      }
      flash("success", `Updated analysis codes on ${dirtyProductIds.length} product${dirtyProductIds.length === 1 ? "" : "s"}`);
      // Refresh so server-side normalisation (e.g. ordering) is reflected.
      await loadData();
    } catch (err) {
      flash("error", err.message);
    }
    setSavingProducts(false);
  }

  // Get values for a given typecode
  function valuesFor(typecode) {
    const group = typeGroups.find(g => g.typecode === typecode);
    return group ? group.values : [];
  }

  if (providerType === "igt") {
    return (
      <div className="form-section">
        <div className="empty-state">
          <p>Analysis codes are an S7000-specific feature. IGT venues use membership types and terms instead.</p>
        </div>
      </div>
    );
  }

  // Build AdminSelect options
  const typeOptions = [
    { value: "", label: "— Select type —" },
    ...typeGroups.map(g => ({ value: g.typecode, label: `${g.typecode} — ${g.name}` }))
  ];

  const contextOptions = CONTEXTS.map(c => ({ value: c.value, label: c.label }));

  const behaviorOptions = BEHAVIORS.map(b => ({ value: b.value, label: b.label, hint: b.desc }));

  function valueOptionsFor(typecode) {
    return [
      { value: "", label: "— None —" },
      ...valuesFor(typecode).map(v => ({ value: v.valuecode, label: `${v.valuecode} — ${v.label || "(no label)"}` }))
    ];
  }

  return (
    <div className="form-section">
      {feedback.msg && <div className={`toast toast-${feedback.type}`} style={{ marginBottom: "1rem" }}>{feedback.msg}</div>}

      <div className="section-header-row">
        <div>
          <h2>Analysis Codes</h2>
          <p className="section-desc">
            Configure S7000 analysis codes sent during member creation, renewal, and updates.
            {types.length > 0 && <span style={{ marginLeft: 8, opacity: 0.7 }}>{typeGroups.length} types, {types.length} values cached</span>}
          </p>
        </div>
        <button className="btn btn-outline" onClick={fetchFromS7000} disabled={fetching} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {Ico.refresh} {fetching ? "Fetching…" : "Fetch from S7000"}
        </button>
      </div>

      {!loaded ? (
        <p style={{ color: "var(--text-muted)", padding: "1rem 0" }}>Loading…</p>
      ) : types.length === 0 ? (
        <div className="empty-state" style={{ padding: "2rem" }}>
          <p>No analysis types cached. Click <strong>Fetch from S7000</strong> to pull the available codes from this venue's S7000 system.</p>
        </div>
      ) : (
        <>
          {/* ── Available Types ─────────────── */}
          <div style={{ marginBottom: "1.5rem" }}>
            <h3 style={{ margin: "0 0 0.5rem", fontSize: "0.95rem", fontWeight: 600 }}>
              Available Analysis Types
              <span style={{ fontWeight: 400, color: "var(--text-muted)", fontSize: "0.82rem", marginLeft: 6 }}>({typeGroups.length})</span>
            </h3>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>Code</th>
                    <th>Name</th>
                    <th style={{ width: 80, textAlign: "right" }}>Values</th>
                    <th style={{ width: 36 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {typeGroups.map(g => (
                    <React.Fragment key={g.typecode}>
                      <tr
                        onClick={() => setExpandedType(expandedType === g.typecode ? null : g.typecode)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>
                          <code style={{ color: "var(--accent)", fontWeight: 600, fontSize: "0.82rem" }}>{g.typecode}</code>
                        </td>
                        <td>{g.name}</td>
                        <td style={{ textAlign: "right", color: "var(--text-muted)" }}>{g.values.length}</td>
                        <td style={{ textAlign: "center" }}>
                          <svg
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                            strokeLinecap="round" strokeLinejoin="round" width="14" height="14"
                            style={{ color: "var(--text-muted)", transition: "transform 0.2s", transform: expandedType === g.typecode ? "rotate(180deg)" : "rotate(0)" }}
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </td>
                      </tr>
                      {expandedType === g.typecode && (
                        <tr>
                          <td colSpan={4} style={{ padding: 0 }}>
                            <div style={{ background: "var(--bg-elevated)", padding: "0.5rem 1rem 0.65rem" }}>
                              <div style={{
                                display: "grid", gridTemplateColumns: "80px 1fr",
                                gap: "0 1rem", fontSize: "0.8rem",
                              }}>
                                <span style={{ fontWeight: 600, color: "var(--text-muted)", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.04em", padding: "4px 0", borderBottom: "1px solid var(--border)" }}>Code</span>
                                <span style={{ fontWeight: 600, color: "var(--text-muted)", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.04em", padding: "4px 0", borderBottom: "1px solid var(--border)" }}>Label</span>
                                {g.values.map(v => (
                                  <React.Fragment key={v.valuecode}>
                                    <code style={{ color: "var(--accent)", padding: "4px 0", fontSize: "0.78rem" }}>{v.valuecode}</code>
                                    <span style={{ color: "var(--text-secondary)", padding: "4px 0" }}>{v.label || "—"}</span>
                                  </React.Fragment>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Rules ─────────────────────── */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
            <h3 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 600 }}>Rules ({rules.length})</h3>
            <button className="btn btn-outline" onClick={addRule} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              {Ico.plus} Add Rule
            </button>
          </div>

          {rules.length === 0 ? (
            <div className="info-box" style={{ flexDirection: "column", gap: 4 }}>
              <span>No rules configured yet. Click <strong>+ Add Rule</strong> to start.</span>
              <span style={{ fontSize: "0.78rem", opacity: 0.7 }}>
                Without rules, product analysis codes are still sent. Rules let you add fixed values or preserve existing S7000 data on top of that.
              </span>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {rules.map((rule, idx) => {
                const typeName = typeGroups.find(g => g.typecode === rule.typecode)?.name || "";
                const behaviorInfo = BEHAVIORS.find(b => b.value === rule.behavior);
                return (
                  <div key={idx} style={{
                    background: "var(--bg-surface)", border: "1px solid var(--border)",
                    borderRadius: 10, overflow: "hidden",
                  }}>
                    {/* Rule header */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.65rem 1rem", background: "var(--bg-elevated)", borderBottom: "1px solid var(--border)" }}>
                      <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Rule {idx + 1}{typeName ? ` — ${typeName}` : ""}
                      </span>
                      <button
                        className="btn-icon-mini"
                        onClick={() => removeRule(idx)}
                        title="Remove rule"
                        style={{ color: "var(--danger)", opacity: 0.7, padding: 2 }}
                      >
                        {Ico.trash}
                      </button>
                    </div>

                    <div style={{ padding: "1rem" }}>
                      {/* Row 1: "When" + "For analysis type" */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
                        <div className="form-field" style={{ margin: 0 }}>
                          <label style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4, color: "var(--text-muted)" }}>When…</label>
                          <AdminSelect
                            value={rule.context}
                            onChange={v => updateRule(idx, "context", v)}
                            options={contextOptions}
                          />
                        </div>
                        <div className="form-field" style={{ margin: 0 }}>
                          <label style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4, color: "var(--text-muted)" }}>For analysis type…</label>
                          <AdminSelect
                            value={rule.typecode}
                            onChange={v => updateRule(idx, "typecode", v)}
                            options={typeOptions}
                            searchable
                            searchPlaceholder="Search types…"
                            placeholder="— Select type —"
                          />
                        </div>
                      </div>

                      {/* Row 2: Behavior */}
                      <div className="form-field" style={{ margin: "0 0 0.5rem" }}>
                        <label style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4, color: "var(--text-muted)" }}>What should happen?</label>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {BEHAVIORS.map(b => (
                            <label key={b.value} style={{
                              display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 12px",
                              borderRadius: 8, cursor: "pointer",
                              border: rule.behavior === b.value ? "1.5px solid var(--accent)" : "1.5px solid var(--border)",
                              background: rule.behavior === b.value ? "var(--accent-soft)" : "transparent",
                              transition: "all 0.15s",
                            }}>
                              <input
                                type="radio" name={`behavior-${idx}`}
                                value={b.value} checked={rule.behavior === b.value}
                                onChange={() => updateRule(idx, "behavior", b.value)}
                                style={{ marginTop: 2, accentColor: "var(--accent)" }}
                              />
                              <div>
                                <div style={{ fontWeight: 600, fontSize: "0.84rem", color: "var(--text)" }}>{b.label}</div>
                                <div style={{ fontSize: "0.76rem", color: "var(--text-muted)", marginTop: 1 }}>{b.desc}</div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Row 3: Default value (shown for fixed + preserve) */}
                      {rule.behavior !== "product_mapped" && (
                        <div className="form-field" style={{ margin: 0 }}>
                          <label style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4, color: "var(--text-muted)" }}>
                            {rule.behavior === "fixed" ? "Value to send" : "Fallback value (if member has none)"}
                          </label>
                          <AdminSelect
                            value={rule.default_valuecode || ""}
                            onChange={v => updateRule(idx, "default_valuecode", v)}
                            options={valueOptionsFor(rule.typecode)}
                            searchable
                            searchPlaceholder="Search values…"
                            placeholder="— None —"
                          />
                        </div>
                      )}

                      {/* Row 4: Per-product code editor (shown for product_mapped) */}
                      {rule.behavior === "product_mapped" && rule.typecode && (
                        <div style={{ marginTop: "0.75rem", border: "1px solid var(--border)", borderRadius: 8, background: "var(--bg-elevated)" }}>
                          <div style={{ padding: "0.55rem 0.85rem", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
                            <div>
                              <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--text)" }}>
                                Value per product
                              </div>
                              <div style={{ fontSize: "0.74rem", color: "var(--text-muted)", marginTop: 1 }}>
                                Set the <code style={{ color: "var(--accent)" }}>{typeName || rule.typecode}</code> code each product sends when chosen.
                              </div>
                            </div>
                          </div>
                          {products.length === 0 ? (
                            <div style={{ padding: "0.85rem", fontSize: "0.82rem", color: "var(--text-muted)" }}>
                              No products configured for this venue yet.
                            </div>
                          ) : (
                            <div style={{ padding: "0.5rem 0.85rem" }}>
                              {products.map(prod => {
                                const current = getProductCode(prod.id, rule.typecode);
                                const dirty = dirtyProductIds.includes(prod.id);
                                return (
                                  <div
                                    key={prod.id}
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 260px",
                                      gap: "0.75rem",
                                      alignItems: "center",
                                      padding: "0.4rem 0",
                                      borderBottom: "1px solid var(--border-light)",
                                    }}
                                  >
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {prod.name}
                                        {dirty && (
                                          <span style={{ marginLeft: 8, fontSize: "0.68rem", color: "var(--warning)", fontWeight: 600 }}>
                                            • unsaved
                                          </span>
                                        )}
                                      </div>
                                      {prod.description && (
                                        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                          {prod.description}
                                        </div>
                                      )}
                                    </div>
                                    <AdminSelect
                                      value={current}
                                      onChange={v => setProductCode(prod.id, rule.typecode, v)}
                                      options={valueOptionsFor(rule.typecode)}
                                      searchable
                                      searchPlaceholder="Search values…"
                                      placeholder="— None —"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {(rules.length > 0 || dirtyProductIds.length > 0) && (
            <div className="form-actions" style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center" }}>
              {rules.length > 0 && (
                <button className="btn btn-primary" onClick={saveRules} disabled={saving} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {Ico.save} {saving ? "Saving…" : "Save Rules"}
                </button>
              )}
              {dirtyProductIds.length > 0 && (
                <button
                  className="btn btn-outline"
                  onClick={saveProductCodes}
                  disabled={savingProducts}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                  title={`${dirtyProductIds.length} product${dirtyProductIds.length === 1 ? "" : "s"} with unsaved changes`}
                >
                  {Ico.save} {savingProducts ? "Saving…" : `Save product codes (${dirtyProductIds.length})`}
                </button>
              )}
            </div>
          )}

          {/* ── Help text ──────────────────── */}
          <div className="info-box" style={{ marginTop: "1.5rem", flexDirection: "column", gap: 2 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, fontSize: "0.84rem", color: "var(--text-secondary)" }}>
              {Ico.info} How this works
            </div>
            <p style={{ margin: "0.4rem 0 0", lineHeight: 1.8, fontSize: "0.82rem" }}>
              Analysis codes are key-value tags S7000 stores on each member (e.g. MEMBER TYPE = Silver, TIERING = None).
              <strong> Product mappings</strong> (set on each product's edit form) define codes sent when a member picks that product.
              <strong> Rules</strong> are optional and let you override or supplement product values — for example, always sending a fixed
              code or preserving the member's existing value during renewals.
            </p>
            <p style={{ marginTop: 6, fontSize: "0.78rem", opacity: 0.8 }}>
              ⚠ S7000's <code>addUpdateMember</code> will <strong>delete all existing analysis data</strong> if <code>analysisArray</code> is omitted.
              Make sure every important type is covered by either a product mapping or a rule.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
